import { ActionReducerMapBuilder } from '@reduxjs/toolkit';
import { PlayerPosition } from 'modules/teams/domain/enums/player-position';
import type { TeamPlayer } from 'modules/teams/domain/interfaces/team-player';
import { toast } from 'react-toastify';
import { USER_DEFAULT_AVATAR } from 'shared/constants/links';
import { Role } from 'shared/types/role';
import { changePlayerRole, deleteTournamentPlayerApplication, getTeamPlayers, upsertTournamentPlayerApplication } from './actions';

export type PlayersSliceState = {
  isGettingPlayers: boolean;
  isLoading: boolean;
  info: TeamPlayer[];
};

type PlayerReducerBuilder = ActionReducerMapBuilder<PlayersSliceState>;

export function createGetTournamentPlayers(builder: PlayerReducerBuilder) {
  builder.addCase(getTeamPlayers.pending, (state) => {
    state.isGettingPlayers = true;
  });

  builder.addCase(getTeamPlayers.fulfilled, (state, { payload }) => {
    state.info = payload.sort((a, b) => b.id - a.id);
    state.isGettingPlayers = false;
  });

  builder.addCase(getTeamPlayers.rejected, (state) => {
    state.isGettingPlayers = false;
  });
}

export function createUpsertTournamentPlayerApplication(builder: PlayerReducerBuilder) {
  builder.addCase(upsertTournamentPlayerApplication.pending, (state) => {
    state.isLoading = true;
  });

  builder.addCase(upsertTournamentPlayerApplication.fulfilled, (state, { payload }) => {
    if (state.info) {
      const teamPlayer: TeamPlayer = {
        id: 0,
        userId: payload.id,
        firstName: payload.name,
        lastName: payload.surname,
        middleName: payload.middleName,
        birthDate: payload.birthDate,
        height: payload.height,
        weight: payload.weight,
        avatar: payload.photo?.externalUrl ?? USER_DEFAULT_AVATAR,
        position: PlayerPosition.FORWARD,
        number: 99,
        isReal: payload.isReal,
        role: payload.role,
      };

      const editingPlayer = state.info.find((player) => player.userId === payload.id);
      let players;

      if (editingPlayer) {
        const editingPlayerIndex = state.info.indexOf(editingPlayer);
        state.info.splice(editingPlayerIndex, 1, { ...editingPlayer, ...teamPlayer, id: editingPlayer.id });
        players = [...state.info].sort((a, b) => b.id - a.id);
      } else {
        players = [...state.info, teamPlayer].sort((a, b) => b.id - a.id);
      }

      if (players?.length) {
        state.info = [...players];
      }
    }
    state.isLoading = false;
  });

  builder.addCase(upsertTournamentPlayerApplication.rejected, (state) => {
    state.isLoading = false;
  });
}

export function createDeleteTournamentPlayerApplication(builder: PlayerReducerBuilder) {
  builder.addCase(deleteTournamentPlayerApplication.pending, (state) => {
    state.isLoading = true;
  });

  builder.addCase(deleteTournamentPlayerApplication.fulfilled, (state, { payload }) => {
    if (state.info) {
      const deletingPlayer = state.info.find((player) => player.userId === payload.userId);
      let players;

      if (deletingPlayer) {
        const deletingPlayerIndex = state.info.indexOf(deletingPlayer);
        state.info.splice(deletingPlayerIndex, 1);
        players = [...state.info].sort((a, b) => b.id - a.id);
      } else {
        players = [...state.info].sort((a, b) => b.id - a.id);
      }

      if (players?.length) {
        state.info = [...players];
      } else {
        state.info = [];
      }
    }
    state.isLoading = false;
  });

  builder.addCase(deleteTournamentPlayerApplication.rejected, (state) => {
    state.isLoading = false;
    toast.error('Не удалось удалить игрока из заявок');
  });
}

export function createChangePlayerRole(builder: PlayerReducerBuilder) {
  builder.addCase(changePlayerRole.fulfilled, (state, { payload, meta }) => {
    if (state.info) {
      const changingPlayer = state.info.find((player) => player.userId === meta.arg.userId);

      if (changingPlayer) {
        changingPlayer.role = meta.arg.role as Role;
      }
    }
  });
}
